import { useEffect, useState } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";
import { BackendServices } from "services";
import { ScientificPublishingStatisticItem } from "services/scientificPublicationsService/models/scientificPublicationsTypes";
import { useTranslation } from "react-i18next";

// Beautiful gradient colors matching the tile theme
const TYPE_COLORS: Record<string, string> = {
  'كتاب': '#667eea',
  'بحث': '#11998e',
  'مقال': '#f5576c',
  'رسالة': '#9796f0',
  'Book': '#667eea',
  'Research': '#11998e',
  'Article': '#f5576c',
  'Letter': '#9796f0',
};

const FALLBACK_COLORS = ['#667eea', '#f093fb', '#11998e', '#fa709a', '#ee0979', '#fbc7d4', '#30cfd0', '#fee140'];

interface ScientificPublishingStatisticsChartProps {
  useMyStats?: boolean;
}

const ScientificPublishingStatisticsChart = ({ useMyStats = false }: ScientificPublishingStatisticsChartProps) => {
  const [statistics, setStatistics] = useState<ScientificPublishingStatisticItem[]>([]);
  const [loading, setLoading] = useState(true);
  const { t, i18n } = useTranslation();

  // Get current language direction
  const isRTL = i18n.language === 'ar';

  useEffect(() => {
    fetchStatistics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language, useMyStats]);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      const response = useMyStats
        ? await BackendServices.scientificPublicationsService.getMyScientificPublishingStatistics()
        : await BackendServices.scientificPublicationsService.getScientificPublishingStatistics();
      if (!response.hasError && response.data?.statistics) {
        setStatistics(response.data.statistics);
      }
    } catch (error) {
      console.error("Error fetching scientific publishing statistics:", error);
    } finally {
      setLoading(false);
    }
  };

  const getColorForType = (name: string, index: number): string => {
    return TYPE_COLORS[name] || FALLBACK_COLORS[index % FALLBACK_COLORS.length];
  };

  // Custom label renderer for RTL support
  const renderCustomLabel = (props: any) => {
    const { cx, cy, midAngle, outerRadius, name, percent } = props;
    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 30;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    const textAnchor = isRTL
      ? (x > cx ? 'end' : 'start')
      : (x > cx ? 'start' : 'end');

    return (
      <text
        x={x}
        y={y}
        textAnchor={textAnchor}
        dominantBaseline="central"
        style={{
          fontSize: '13px',
          fontWeight: 500,
          fill: '#2c3e50',
          direction: isRTL ? 'rtl' : 'ltr',
        }}
      >
        {`${name}: ${((percent ?? 0) * 100).toFixed(0)}%`}
      </text>
    );
  };

  if (loading) {
    return (
      <div
        className="card"
        style={{
          backgroundColor: 'white',
          border: 'none',
          borderRadius: '12px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)',
          minHeight: '400px',
        }}
      >
        <div className="d-flex justify-content-center align-items-center h-100" style={{ minHeight: 400 }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (statistics.length === 0) {
    return (
      <div
        className="card"
        style={{
          backgroundColor: 'white',
          border: 'none',
          borderRadius: '12px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)',
        }}
      >
        <div className="text-center text-muted p-4">
          {t("Common:noData")}
        </div>
      </div>
    );
  }

  return (
    <div
      className="card"
      style={{
        backgroundColor: 'white',
        border: 'none',
        borderRadius: '12px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)',
        transition: 'all 0.3s ease',
        overflow: 'hidden',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 8px 16px rgba(0, 0, 0, 0.12)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.07)';
      }}
    >
      <div className="card-header" style={{ backgroundColor: 'white', border: 'none', paddingTop: '1.5rem', paddingBottom: '1rem' }}>
        <h5 className="card-title mb-0 fw-bold" style={{ color: '#2c3e50', fontSize: '1.25rem' }}>
          {t("Dashboard:scientificPublishingStatistics")}
        </h5>
      </div>
      <div className="card-body p-4" style={{ backgroundColor: 'white', direction: isRTL ? 'rtl' : 'ltr' }}>
        <ResponsiveContainer width="100%" height={380}>
          <PieChart>
            <Pie
              data={statistics}
              cx="50%"
              cy="50%"
              innerRadius={70}
              outerRadius={120}
              fill="#8884d8"
              paddingAngle={3}
              dataKey="count"
              label={renderCustomLabel}
              labelLine={{
                stroke: '#cbd5e0',
                strokeWidth: 1,
              }}
              style={{ direction: isRTL ? 'rtl' : 'ltr' }}
            >
              {statistics.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={getColorForType(entry.name, index)}
                  stroke="white"
                  strokeWidth={2}
                />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{
                backgroundColor: 'white',
                border: 'none',
                borderRadius: '8px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                direction: isRTL ? 'rtl' : 'ltr',
              }}
              formatter={(value: any, name: any) => [value, name]}
            />
            <Legend
              verticalAlign="bottom"
              height={36}
              iconType="circle"
              wrapperStyle={{ direction: isRTL ? 'rtl' : 'ltr' }}
              formatter={(value, entry: any) => (
                <span style={{ color: '#2c3e50', fontWeight: 500 }}>
                  {value} ({entry.payload.count})
                </span>
              )}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default ScientificPublishingStatisticsChart;